import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import {
  CreditCard,
  ArrowLeft,
  Users,
  Check,
  ChevronRight,
  Sparkles,
  ShieldCheck,
  Zap,
  Flame,
  Crown,
} from "lucide-react";
import {
  membershipConfig,
  formatMembership,
  normaliseTier,
} from "@/lib/membershipUtils";

const TIERS = [
  {
    id: "foundation",
    name: "Foundation",
    price: "$29/mo",
    icon: Zap,
    color: "from-blue-500/20 to-sky-600/10 text-sky-400 border-sky-500/30",
    features: [
      "Access to standard workout plans",
      "Daily progress & check-in logging",
      "Macro calculations",
      "Community chat",
    ],
  },
  {
    id: "momentum",
    name: "Momentum",
    price: "$49/mo",
    icon: Flame,
    color: "from-amber-500/20 to-orange-600/10 text-amber-400 border-amber-500/30",
    features: [
      "All Foundation features",
      "Custom workout splits & library",
      "Nutrition & meal planning templates",
      "AI Coach assistant access",
    ],
  },
  {
    id: "strength",
    name: "Strength & Muscle",
    price: "$89/mo",
    icon: ShieldCheck,
    color: "from-emerald-500/20 to-teal-600/10 text-emerald-400 border-emerald-500/30",
    features: [
      "All Momentum features",
      "Advanced exercise analytics",
      "Assigned certified coach oversight",
      "Personalized weekly review",
    ],
  },
  {
    id: "elite_coaching",
    name: "Elite 1-on-1 Coaching",
    price: "$199/mo",
    icon: Crown,
    color: "from-purple-500/20 to-indigo-600/10 text-purple-400 border-purple-500/30",
    features: [
      "All Strength features",
      "Live 1-on-1 Agora video coaching sessions",
      "AI Vision instant meal scanner",
      "Direct trainer chat priority",
      "Unused session rollover banking",
    ],
  },
];

export default function AdminSubscriptionsSection({
  onBack,
  onNavigate,
}) {
  const { data: members = [], isLoading } = useQuery({
    queryKey: ["admin_members"],
    queryFn: () => base44.entities.User.list(),
  });

  const tierCounts = useMemo(() => {
    const counts = {
      foundation: 0,
      momentum: 0,
      strength: 0,
      elite_coaching: 0,
      other: 0,
    };
    members.forEach((m) => {
      const tier = normaliseTier(m.membership_type);
      if (counts[tier] !== undefined) {
        counts[tier]++;
      } else {
        counts.other++;
      }
    });
    return counts;
  }, [members]);

  return (
    <div className="space-y-4 pb-20">
      {/* Top Bar with Back navigation & Title */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="p-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-all cursor-pointer"
          aria-label="Back to Command Center"
        >
          <ArrowLeft className="w-4 h-4 text-sky-400" />
        </button>
        <div>
          <h1 className="text-lg font-bold text-white leading-tight">Subscriptions & Tiers</h1>
          <p className="text-[11px] text-slate-400">
            Membership tier distribution & entitlements
          </p>
        </div>
      </div>

      {/* Tier Cards */}
      <div className="space-y-3">
        {TIERS.map((tier) => {
          const Icon = tier.icon;
          const count = tierCounts[tier.id] || 0;

          return (
            <div
              key={tier.id}
              className="p-4 rounded-2xl bg-gradient-to-br from-[#08152B] to-[#040C1A] border border-white/10 shadow-md space-y-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={`p-2 rounded-xl bg-gradient-to-br border ${tier.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white leading-tight">{tier.name}</h3>
                    <p className="text-[11px] text-slate-400">{tier.price}</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => onNavigate("members", tier.id)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 hover:bg-sky-500/20 border border-white/10 text-sky-400 text-xs font-bold transition-all cursor-pointer"
                >
                  <Users className="w-3.5 h-3.5" />
                  <span>{count} member{count !== 1 ? "s" : ""}</span>
                  <ChevronRight className="w-3 h-3 text-slate-400" />
                </button>
              </div>

              {/* Entitlements */}
              <div className="space-y-1.5 pt-1 border-t border-white/5">
                {tier.features.map((feat, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-[11px] text-slate-300">
                    <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
